import { useState } from "react";
import { ArrowsClockwise, PlugsConnected } from "@phosphor-icons/react";
import { Button, Fact, Facts, Field, PageHeader, Section, Select, Table, TBody, TD, TDNum, TH, THead, TR, formatCount } from "@ghezaa/ui";
import { formatDateTime } from "@ghezaa/shell";
import type { BcStatus } from "@/api/client";
import { useLinkCompany, useReadBc, useReadSales } from "@/api/mutations";
import { useBcCompanies, useBcStatus } from "@/api/queries";
import { day } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";

/**
 * The market's Business Central company and what Sales last read from it. Sales reads with its own registration, which
 * BC gives D365 READ only: nothing on this page can change BC.
 */
export function BusinessCentralPage() {
  const market = useCurrentMarket();
  const status = useBcStatus(market.code);
  const read = useReadBc(market.code);
  const link = status.data?.link ?? null;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Business Central"
        description={link ? `${market.name} reads ${link.companyName} in ${link.environment}.` : `Link ${market.name} to its company to read its customers and salespeople.`}
        actions={
          link && (
            <Button onClick={() => read.mutate()} disabled={read.isPending}>
              <ArrowsClockwise />
              {read.isPending ? "Reading…" : "Read now"}
            </Button>
          )
        }
      />
      {status.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load the page: {status.error.message}</p>}
      {read.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">{read.error.message}</p>}
      {status.data && (
        <>
          {!status.data.configured && (
            <p className="flex items-center gap-2 bg-warning-soft px-3 py-2 text-sm text-warning">
              <PlugsConnected />
              Sales has no Business Central credentials yet: SALES_BC_* in the server's .env, or user-secrets locally.
            </p>
          )}
          <CompanySection status={status.data} />
          {link && <ReadSection status={status.data} />}
          {link && <SalesSection status={status.data} />}
        </>
      )}
    </div>
  );
}

function CompanySection({ status }: { status: BcStatus }) {
  const market = useCurrentMarket();
  const link = status.link;
  const [changing, setChanging] = useState(false);
  const choosing = status.configured && (!link || changing);
  const companies = useBcCompanies(market.code, choosing);
  const linkCompany = useLinkCompany(market.code);
  const [chosen, setChosen] = useState("");

  return (
    <Section n="01" title="Company">
      <div className="flex flex-col gap-5">
        {link && (
          <Facts>
            <Fact label="Company" sub={link.environment}>{link.companyName}</Fact>
            <Fact label="Linked" sub={`by ${link.linkedBy}`}>
              <span className="tabular-nums">{formatDateTime(link.linkedAt)}</span>
            </Fact>
          </Facts>
        )}
        {link && !changing && status.configured && (
          <div>
            <Button size="sm" onClick={() => setChanging(true)}>
              Link another company
            </Button>
          </div>
        )}
        {choosing && (
          <form
            className="flex max-w-[480px] flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (chosen) linkCompany.mutate(chosen, { onSuccess: () => setChanging(false) });
            }}
          >
            <Field label="Company" htmlFor="bc-company" hint={link ? "Linking another company reads it in place of this one." : undefined} error={companies.isError ? companies.error.message : undefined}>
              <Select id="bc-company" value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={!companies.data}>
                <option value="">{companies.isPending ? "Reading the companies…" : "Choose a company"}</option>
                {companies.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.displayName && c.displayName !== c.name ? ` · ${c.displayName}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
            {linkCompany.isError && <p className="text-xs text-danger">{linkCompany.error.message}</p>}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={!chosen || linkCompany.isPending}>
                {linkCompany.isPending ? "Linking and reading…" : "Link and read"}
              </Button>
              {link && (
                <Button type="button" onClick={() => setChanging(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
        )}
      </div>
    </Section>
  );
}

function ReadSection({ status }: { status: BcStatus }) {
  const read = status.link!.read;
  const counts = status.counts;
  const rows: [string, number][] = [
    ["Salespeople", counts.salespeople],
    ["Customers", counts.customers],
    ["Outlets (ship-to addresses)", counts.outlets],
    ["Items", counts.items],
    ["Item categories", counts.categories],
    ["Dimension values", counts.dimensionValues],
  ];
  return (
    <Section n="02" title="Last read" note="Every hour, and at Read now." measure="narrow">
      <div className="flex flex-col gap-4">
        {read.failedAt && read.problem && (
          <div className="flex flex-col gap-1 bg-danger-soft px-3 py-2 text-sm text-danger">
            <p>
              The read of {formatDateTime(read.failedAt)} failed: {read.problem}
            </p>
            {read.error && <p className="text-xs">Business Central said: {read.error}</p>}
          </div>
        )}
        <p className="text-sm text-ink-2">{read.at ? `Read ${formatDateTime(read.at)}.` : "Not read yet."}</p>
        <Table density="compact">
          <THead>
            <TR>
              <TH>Record</TH>
              <TH className="text-end">Read</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map(([label, count]) => (
              <TR key={label}>
                <TD>{label}</TD>
                <TDNum>{formatCount(count)}</TDNum>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </Section>
  );
}

/** The sales read: BC's value entries for invoices and credit memos, added as they are posted. */
function SalesSection({ status }: { status: BcStatus }) {
  const market = useCurrentMarket();
  const readSales = useReadSales(market.code);
  const sales = status.sales;
  const read = sales.read;
  return (
    <Section n="03" title="Sales" note="BC's value entries of invoices and credit memos, every half hour and at Read sales now." measure="narrow">
      <div className="flex flex-col gap-4">
        {read.failedAt && read.problem && (
          <div className="flex flex-col gap-1 bg-danger-soft px-3 py-2 text-sm text-danger">
            <p>
              The sales read of {formatDateTime(read.failedAt)} failed: {read.problem}
            </p>
            {read.error && <p className="text-xs">Business Central said: {read.error}</p>}
          </div>
        )}
        <Facts>
          <Fact label="Entries" sub={sales.lastEntryNo ? `Up to entry ${sales.lastEntryNo}` : undefined}>
            <span className="tabular-nums">{formatCount(sales.entries)}</span>
          </Fact>
          <Fact label="Posted" sub={sales.to ? `to ${day(sales.to)}` : undefined}>
            {sales.from ? <span className="tabular-nums">{day(sales.from)}</span> : <span className="text-ink-3">None yet</span>}
          </Fact>
          <Fact label="Read">{read.at ? <span className="tabular-nums">{formatDateTime(read.at)}</span> : <span className="text-ink-3">Not yet</span>}</Fact>
        </Facts>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => readSales.mutate(false)} disabled={readSales.isPending}>
            <ArrowsClockwise />
            {readSales.isPending ? "Reading…" : "Read sales now"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => readSales.mutate(true)} disabled={readSales.isPending}>
            Read all again
          </Button>
        </div>
        {readSales.isError && <p className="text-xs text-danger">{readSales.error.message}</p>}
      </div>
    </Section>
  );
}
